/**
 * G1 — park return-before-OIDC for embedded SignIn on post-pay / QR recovery /
 * refund CTAs. Prefer durable payment-resume for checkout/donate; else platform
 * park for `/account/payments/.../qr`, `/{tenant}/post-kiosk-failure`, and other
 * allowlisted platform targets. Successful platform parks clear stale payment-resume
 * so it cannot beat the intentional platform park in resolveOidcLoginReturnTo.
 */

import {
  inferPaymentResumeReturnSource,
  isPaymentResumeReturnPath,
} from '../../shared/session/isPaymentResumeReturnPath.js';
import {
  clearPaymentResumeReturnTarget,
  parkPaymentResumeReturnTarget,
  savePlatformReturnTarget,
  validatePlatformReturnTarget,
} from '../../shared/session/returnTarget.js';

export type PostPayAuthReturnParkMode = 'payment-resume' | 'platform' | 'none';

export interface ParkPostPayAuthReturnResult {
  parked: boolean;
  mode: PostPayAuthReturnParkMode;
}

/** Bare or tenant-scoped account pending-QR recovery path. */
function looksLikeAccountPaymentsQrPath(pathWithQuery: string): boolean {
  const withoutHash = pathWithQuery.split('#', 1)[0] ?? pathWithQuery;
  const pathOnly =
    (withoutHash.split('?', 1)[0] ?? withoutHash).replace(/\/+$/, '') || '/';
  return (
    /^\/account\/payments\/[^/]+\/qr$/.test(pathOnly) ||
    /^\/[^/]+\/account\/payments\/[^/]+\/qr$/.test(pathOnly)
  );
}

/** Tenant-scoped post-kiosk failure deep-link (`/{tenant}/post-kiosk-failure`). */
function looksLikePostKioskFailurePath(pathWithQuery: string): boolean {
  const withoutHash = pathWithQuery.split('#', 1)[0] ?? pathWithQuery;
  const pathOnly =
    (withoutHash.split('?', 1)[0] ?? withoutHash).replace(/\/+$/, '') || '/';
  return /^\/[^/]+\/post-kiosk-failure$/.test(pathOnly);
}

/**
 * Park a post-pay / QR / refund return target before opening embedded SignIn
 * (or navigating to OIDC). Fail-closed: `parked: false` when storage/validate
 * rejects.
 */
export function parkPostPayAuthReturnTarget(
  pathWithQuery: string,
  tenantCode: string,
): ParkPostPayAuthReturnResult {
  if (isPaymentResumeReturnPath(pathWithQuery)) {
    const source = inferPaymentResumeReturnSource(pathWithQuery) ?? 'checkout';
    const parked = parkPaymentResumeReturnTarget(pathWithQuery, {
      tenantCode,
      source,
    });
    if (parked !== null) {
      // Intentional payment-resume park — do not clear it.
      return { parked: true, mode: 'payment-resume' };
    }
    return { parked: false, mode: 'none' };
  }

  if (
    looksLikeAccountPaymentsQrPath(pathWithQuery) ||
    looksLikePostKioskFailurePath(pathWithQuery) ||
    validatePlatformReturnTarget(pathWithQuery) !== null
  ) {
    const saved = savePlatformReturnTarget(pathWithQuery);
    if (saved !== null) {
      // G5 — intentional platform park wins over stale checkout resume.
      clearPaymentResumeReturnTarget();
      return { parked: true, mode: 'platform' };
    }
    return { parked: false, mode: 'none' };
  }

  return { parked: false, mode: 'none' };
}
